import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect
} from "react";
import { UserData } from "@/entities/users/types";
import { useReport } from "@/entities/reporting/hooks";
import {
  ConversionRatesFunnelData,
  ConversionRatesDirectReportsResponse,
  ReportType,
  SettingsPeriod
} from "@/entities/reporting/types";
import { camelCase, debounce, isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import ConversionRates, {
  ConversionRatesChartDataType
} from "./ConversionRates";
import { createDataMapById } from "@/utils/datamaps";
import { getColorForMilestoneByType } from "@/components/shared/Milestones";
import { DealMilestone } from "@/pages/deals/types";
import { reducePrecision } from "@/utils/numbers";
import { useDealStages } from "@/entities/settings/deal/hooks";
import { useDirectReportsCheck } from "@/components/shared/useDirectReportsCheck";

interface Props {
  userData?: UserData;
  isAE: boolean;
}

const ConversionRatesWrapper: React.FC<Props> = ({ userData, isAE }) => {
  const { t } = useTranslation();

  const stagesResult = useDealStages();
  const stages = stagesResult.data?.stages;

  const [reportPeriod, setReportPeriod] = useState(SettingsPeriod.ALL_TIMES);

  // Keep track of the last successful data using ref to avoid re-render loops
  const lastValidReportRef = useRef<ConversionRatesFunnelData | null>(null);

  const { report, isLoading } = useReport(
    ReportType.CONVERSION_RATES,
    reportPeriod
  );

  const { hasDirectReports, directReportsData } =
    useDirectReportsCheck<ConversionRatesDirectReportsResponse>(
      userData?.id,
      reportPeriod,
      !isAE,
      {
        reportType: ReportType.CONVERSION_RATES,
        responseKey: "conversionRates"
      }
    );

  const debouncedPeriodSelectRef = useRef(
    debounce((period: SettingsPeriod) => {
      setReportPeriod(period);
    }, 300)
  );

  useEffect(() => {
    const debouncedFn = debouncedPeriodSelectRef.current;
    return () => {
      debouncedFn.cancel();
    };
  }, []);

  const debouncedPeriodSelect = useCallback((period: SettingsPeriod) => {
    debouncedPeriodSelectRef.current(period);
  }, []);

  const stagesDataMap = useMemo(
    () => (stages ? createDataMapById(stages) : {}),
    [stages]
  );

  const currentReport = report?.conversionRates?.funnel ?? undefined;

  useEffect(() => {
    if (currentReport) {
      lastValidReportRef.current = currentReport;
    }
  }, [currentReport]);

  const conversionRatesReport: ConversionRatesFunnelData =
    currentReport ?? lastValidReportRef.current ?? undefined;

  const processedStages = useMemo(() => {
    if (
      !report?.conversionRates ||
      !conversionRatesReport ||
      isEmpty(stagesDataMap)
    ) {
      return undefined;
    }

    return conversionRatesReport.stages
      .map(
        (
          stage
        ): (ConversionRatesChartDataType & { stageOrder: number }) | null => {
          const stageId = stage.id.toString();
          const stageData = stagesDataMap[stageId];
          if (!stageData) return null;

          return {
            id: stageId,
            value: reducePrecision(stage.conversions.allToCurrent || 0),
            percentageToWin: reducePrecision(
              stage.conversions.currentToWin || 0
            ),
            label: stageData.name ?? t("unknown"),
            color: getColorForMilestoneByType(stageData.milestone, "border"),
            milestone: stageData.milestone,
            team: reducePrecision(stage.conversions.team || 0),
            org: reducePrecision(stage.conversions.org || 0),
            stageOrder: stageData.stageOrder
          };
        }
      )
      .filter((stage): stage is NonNullable<typeof stage> => stage !== null)
      .sort((a, b) => a.stageOrder - b.stageOrder)
      .map(({ stageOrder, ...rest }) => rest); // Remove stageOrder from final output
  }, [stagesDataMap, report, conversionRatesReport, t]);

  const processedMilestones = useMemo(() => {
    if (
      !report?.conversionRates ||
      !conversionRatesReport ||
      !processedStages ||
      isEmpty(conversionRatesReport.milestones)
    ) {
      return undefined;
    }

    const milestonesWithStages = new Set(
      processedStages.map((stage) => Number(stage.milestone))
    );

    return conversionRatesReport.milestones
      .filter((milestone) => milestonesWithStages.has(milestone.id))
      .map((milestone): ConversionRatesChartDataType => {
        const milestoneId = milestone.id as DealMilestone;
        return {
          id: milestoneId.toString(),
          value: reducePrecision(milestone.conversions.allToCurrent || 0),
          percentageToWin: reducePrecision(
            milestone.conversions.currentToWin || 0
          ),
          label: t(
            `deals.dealView.milestone.${camelCase(DealMilestone[milestoneId])}`
          ),
          color: getColorForMilestoneByType(milestone.id),
          milestone: milestoneId,
          team: reducePrecision(milestone.conversions.team || 0),
          org: reducePrecision(milestone.conversions.org || 0),
          industry: reducePrecision(milestone.conversions.industry || 0)
        };
      });
  }, [report, conversionRatesReport, processedStages, t]);

  const processedReport = useMemo(() => {
    return {
      stages: processedStages ?? [],
      milestones: processedMilestones ?? []
    };
  }, [processedStages, processedMilestones]);

  return (
    <ConversionRates
      chartData={processedReport}
      userData={userData}
      period={reportPeriod}
      periodSelect={debouncedPeriodSelect}
      isLoading={isLoading}
      isAE={isAE}
      drillDownHidden={!hasDirectReports}
      directReportsData={directReportsData}
    />
  );
};

export default ConversionRatesWrapper;
